import React from 'react';
import { Box, Typography } from '@mui/material';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { brand } from '../../theme';
import { formatTimecode } from '../../utils/timecode';
import StatusChip from '../ui/StatusChip';
import EmptyState from '../ui/EmptyState';
import { taskEnd, taskStart, taskTypeLabel } from '../EditorWorkspace/taskMeta';
import type { ProjectTask } from './types';

interface TaskListPanelProps {
  tasks: ProjectTask[];
  fps: number;
}

const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/** Only High and Urgent are worth calling out; the rest is the default. */
const priorityCallout = (priority?: string | null) => {
  const key = String(priority || '').toUpperCase();
  if (key === 'URGENT') return 'Urgent';
  if (key === 'HIGH' || key === 'CRITICAL') return 'High priority';
  return null;
};

/** The editor's task list, read-only: status, what kind of work, and where in the footage. */
const TaskListPanel: React.FC<TaskListPanelProps> = ({ tasks, fps }) => {
  if (tasks.length === 0) {
    return (
      <EmptyState
        compact
        icon={<TaskAltOutlinedIcon />}
        title="No tasks yet"
        description="Your editor’s task list for this project appears here once they plan the work."
      />
    );
  }

  return (
    <Box component="ol" aria-label="Tasks" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {tasks.map((task, index) => {
        const start = taskStart(task);
        const end = taskEnd(task);
        const span = start === null ? null : `${formatTimecode(start, fps)}${end !== null ? ` – ${formatTimecode(end, fps)}` : ''}`;
        const callout = priorityCallout(task.priority);
        return (
          <Box
            component="li"
            key={task.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' },
              columnGap: 2,
              rowGap: 0.75,
              alignItems: 'start',
              px: 2.5,
              py: 1.75,
              borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
            }}
          >
            <Box sx={{ pt: { sm: 0.25 } }}>
              <StatusChip kind="task" status={task.status} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{task.title}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  columnGap: 1.5,
                  rowGap: 0.25,
                  mt: 0.25,
                  color: 'text.secondary',
                  fontSize: '0.8125rem',
                }}
              >
                <span>{taskTypeLabel(task.type)}</span>
                {span && (
                  <Box component="span" sx={{ fontFamily: TIMECODE_FONT, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                    {span}
                  </Box>
                )}
                {callout && (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.primary', fontWeight: 600 }}>
                    <FlagOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
                    {callout}
                  </Box>
                )}
              </Box>
              {task.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {task.description}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default TaskListPanel;
